import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { ArrowLeft, Mail, PlayCircle, ImageDown } from 'lucide-react';
import { toast } from 'sonner';
import ProductInfoTabs from '@/components/ProductInfoTabs';
import ProductDownloadsCard from '@/components/ProductDownloadsCard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_IMG = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#FBF0F6'/><stop offset='1' stop-color='#C2478A'/></linearGradient></defs><rect width='600' height='600' fill='url(#g)'/><text x='300' y='320' text-anchor='middle' font-family='Georgia, serif' font-style='italic' font-size='140' fill='#0A0A0A' opacity='0.4'>IJE</text></svg>`)}`;

const resolveImg = (u) => {
  if (!u) return FALLBACK_IMG;
  if (u.startsWith('http') || u.startsWith('data:')) return u;
  if (u.startsWith('/api/')) return `${API_BASE}${u}`;
  return u;
};

const fmt = (n) => Number(n || 0).toFixed(2);

/**
 * Public product detail — same rich content as the internal PDP
 * (description / specs / additional info / documents) but reachable without
 * auth via /c/:slug/p/:sku. Pricing honours the catalog's pricing_mode.
 */
export default function PublicProductDetail() {
  const { slug, sku } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    setLoading(true);
    setErr(null);
    api
      .get(`/public/catalogs/${slug}/products/${encodeURIComponent(sku)}${token ? `?token=${encodeURIComponent(token)}` : ''}`)
      .then((r) => {
        setData(r.data);
        setActiveImage(0);
        setShowVideo(false);
      })
      .catch((e) => setErr(e.response?.status === 404 ? 'This product is no longer in the catalog.' : 'Failed to load product.'))
      .finally(() => setLoading(false));
  }, [slug, sku, token]);

  if (loading) return <div className="container-x py-20 sans text-neutral-500">Loading…</div>;
  if (err) return (
    <div className="container-x py-20 space-y-4">
      <p className="sans text-neutral-500">{err}</p>
      <Link to={`/c/${slug}`} className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-2">
        <ArrowLeft size={14} /> Back to catalog
      </Link>
    </div>
  );

  const { catalog, product } = data;
  const images = (product.images && product.images.length ? product.images : [FALLBACK_IMG]).map(resolveImg);
  const wholesale = Number(product.wholesale_price || 0);
  const display = Number(product.display_price || wholesale);
  const msrp = Number(product.msrp || 0);
  const hasDiscount = display < wholesale;
  const showCaseMsrp = product.source === 'neurosource' && msrp > 0 && product.case_pack_size;
  const casePack = Number(product.case_pack_size || 0);
  const msrpCase = showCaseMsrp ? msrp * casePack : 0;

  const mailtoBody = encodeURIComponent(
    `Hello IJE HAUS,\n\nI'd like to request pricing for:\n\n${product.name} (SKU ${product.sku})\nFrom catalog: ${catalog.name}\n\n— Sent from ${window.location.href}`
  );
  const mailtoSubject = encodeURIComponent(`Quote request — ${product.name}`);
  const mailtoHref = `mailto:priscila@ijehaus.com?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <div className="container-x py-6 md:py-12 pb-24 md:pb-12" data-testid="public-pdp">
      <Link
        to={`/c/${slug}`}
        className="sans text-xs tracking-widest uppercase text-neutral-500 hover:text-black inline-flex items-center gap-2 mb-4 md:mb-6"
        data-testid="public-pdp-back"
      >
        <ArrowLeft size={14} /> Back to {catalog.name}
      </Link>

      <div className="grid gap-6 md:gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          {product.video_url && showVideo ? (
            <div className="aspect-square bg-black overflow-hidden border border-neutral-200 relative">
              <video
                src={product.video_url}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
                data-testid="public-pdp-video"
              />
              <button
                type="button"
                onClick={() => setShowVideo(false)}
                className="absolute top-3 right-3 bg-white/90 px-3 py-1 sans text-xs tracking-widest uppercase"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="aspect-square bg-white overflow-hidden border border-neutral-200 relative">
              <img src={images[activeImage]} alt={product.name} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
              {product.video_url && (
                <button
                  type="button"
                  onClick={() => setShowVideo(true)}
                  className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/85 text-white px-3 py-2 sans text-xs tracking-widest uppercase hover:bg-black transition-colors"
                >
                  <PlayCircle size={14} /> Watch Video
                </button>
              )}
            </div>
          )}

          {images.length > 1 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {images.map((src, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => { setActiveImage(i); setShowVideo(false); }}
                  className={`aspect-square border overflow-hidden ${activeImage === i && !showVideo ? 'border-black' : 'border-neutral-200'}`}
                  data-testid={`public-pdp-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                </button>
              ))}
            </div>
          )}

          {(product.images && product.images.length > 0) && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const resp = await api.get(
                    `/public/catalogs/${slug}/products/${encodeURIComponent(sku)}/images.zip${token ? `?token=${encodeURIComponent(token)}` : ''}`,
                    { responseType: 'blob' },
                  );
                  const blobUrl = URL.createObjectURL(resp.data);
                  const a = document.createElement('a');
                  a.href = blobUrl;
                  a.download = `${sku}-images.zip`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
                } catch {
                  toast.error('Could not download images');
                }
              }}
              className="btn-outline w-full mt-4 justify-center"
              data-testid="public-pdp-download-images"
            >
              <ImageDown size={14} /> Download all images (ZIP)
            </button>
          )}

          <ProductDownloadsCard documents={product.documents} />
        </div>

        {/* Info */}
        <div>
          <p className="eyebrow">{product.brand}</p>
          <h1 className="h-display text-2xl md:text-4xl mt-2 md:mt-3" data-testid="public-pdp-title">{product.name}</h1>
          <p className="sans text-xs tracking-widest uppercase text-neutral-500 mt-2">
            SKU {product.sku}
            {product.upc ? ` · UPC ${product.upc}` : ''}
            {product.variant ? ` · ${product.variant}` : ''}
          </p>

          {/* Pricing */}
          <div className="mt-5 md:mt-6 py-5 border-y border-neutral-200" data-testid="public-pdp-price">
            <p className="sans text-xs text-neutral-500 tracking-widest uppercase">
              MSRP <span className="text-neutral-700">${fmt(msrp)}{showCaseMsrp ? ' /unit' : ''}</span>
              {showCaseMsrp && (
                <span className="text-neutral-500"> · <span className="text-neutral-700">${fmt(msrpCase)}</span> /case of {casePack}</span>
              )}
            </p>
            <p className="sans text-sm mt-2 flex items-baseline gap-3 flex-wrap">
              <span className="text-neutral-500 tracking-widest uppercase text-xs">Wholesale</span>
              {hasDiscount ? (
                <>
                  <span className="line-through text-neutral-500 text-lg serif">${fmt(wholesale)}</span>
                  <span className="serif text-3xl md:text-4xl pink-gradient-text">${fmt(display)}</span>
                  <span
                    className="sans text-[0.65rem] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full"
                    style={{ background: 'var(--ije-pink-tint)', color: 'var(--ije-pink-dark)' }}
                  >
                    Preview {product.catalog_discount_pct}% off
                  </span>
                </>
              ) : (
                <span className="serif text-3xl md:text-4xl" style={{ color: 'var(--ije-black)' }}>${fmt(wholesale)}</span>
              )}
            </p>
            {product.case_pack_size && (
              <p className="sans text-[11px] text-neutral-500 mt-3 tracking-[0.18em] uppercase">
                Case of {product.case_pack_size} · MOQ {product.min_order_qty || 1}
              </p>
            )}
          </div>

          <div className="mt-6 hidden md:block">
            <a
              href={mailtoHref}
              className="btn-primary inline-flex"
              data-testid="public-pdp-contact"
            >
              <Mail size={16} /> Contact to Order
            </a>
            <p className="sans text-xs text-neutral-500 mt-3 tracking-wider">
              This is a preview catalog. Reach out to priscila@ijehaus.com to place a wholesale order.
            </p>
          </div>
        </div>
      </div>

      <ProductInfoTabs product={product} />

      {/* Mobile sticky action bar */}
      <div className="fixed inset-x-0 bottom-0 md:hidden bg-white border-t border-neutral-200 px-4 py-3 z-40" data-testid="public-pdp-mobile-bar">
        <a
          href={mailtoHref}
          className="btn-primary w-full justify-center"
          data-testid="public-pdp-contact-mobile"
        >
          <Mail size={16} /> Contact to Order
        </a>
      </div>
    </div>
  );
}
